/**
 * Доменные типы агента ОСНОВА.Совещания (автопротоколирование, AI-PROTO-2026).
 *
 * Зеркало DTO роута `/api/v1/agents/meetings/info` — карточка агента (описание +
 * перечень модулей витрины). snake_case 1:1 с backend. Предметные сущности
 * (записи, протоколы, поручения) добавляются сюда же по мере вывода фич на фронт.
 */

/** Статус модуля агента (витрина дашборда). */
export type MeetingsModuleStatus = 'planned' | 'active';

/** Один модуль агента ОСНОВА.Совещания. */
export interface MeetingsModule {
  /** Код модуля (стабильный идентификатор, латиница). */
  code: string;
  /** Человекочитаемое название модуля. */
  title: string;
  /** Текущий статус модуля. */
  status: MeetingsModuleStatus;
}

/** Карточка агента ОСНОВА.Совещания: описание + перечень модулей. */
export interface MeetingsInfo {
  agent_code: string;
  title: string;
  description: string;
  modules: MeetingsModule[];
}

/**
 * Отдельное совещание (зеркало DTO `/agents/meetings/meetings`). snake_case 1:1
 * с backend. Жизненный цикл записи: processing → transcribed → protocol_draft →
 * approved | protocol_declined; `failed` — сбой обработки.
 */
export interface Meeting {
  id: string;
  series_id: string | null;
  project_id?: string | null;
  contract_id?: string | null;
  title: string;
  scheduled_at: string | null;
  organizer_id: string | null;
  status: string;
  note: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Проставлен, если совещание помечено на удаление (soft-delete, зачистка 30 дней). */
  deleted_at: string | null;
  /** Отдел совещания целиком (классификация L-2, блок E): id справочника + дословное имя. */
  department_id: string | null;
  department_name: string | null;
}

/** Серия регулярных совещаний (планёрка по объекту, штаб, ВКС отдела). */
export interface MeetingSeries {
  id: string;
  title: string;
  organizer_id?: string | null;
  department_id: string | null;
  project_id: string | null;
  contract_id: string | null;
  note?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at?: string;
}

/** Человекочитаемая подпись статуса совещания (неизвестный → сам код). */
export function meetingStatusLabel(status: string): string {
  switch (status) {
    case 'processing':
      return 'обработка…';
    case 'transcribed':
      return 'расшифровано';
    case 'protocol_draft':
      return 'черновик протокола';
    case 'approved':
      return 'утверждён';
    case 'protocol_declined':
      return 'отклонён';
    case 'failed':
      return 'ошибка';
    case 'created':
      return 'создано';
    default:
      return status;
  }
}

/** Тон статус-метки: утверждён — success, сбой/отклонён — danger, иначе нейтраль. */
export function meetingStatusTone(status: string): 'success' | 'danger' | 'neutral' {
  if (status === 'approved') return 'success';
  if (status === 'failed' || status === 'protocol_declined') return 'danger';
  return 'neutral';
}

/** Дата-время ISO → `24.06.2026, 14:30` (локаль ru). Пусто/невалид → прочерк/как есть. */
export function fmtMeetingDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Дата (DATE `ГГГГ-ММ-ДД`) → `дд.мм.гггг` (локаль ru). Пусто → прочерк. */
export function fmtDueDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Кандидат-учётка для привязки участника/сопоставления (зеркало Rust `UserCandidate`). */
export interface UserCandidate {
  id: string;
  full_name: string;
  department: string | null;
}

/** Черновик/утверждённый протокол совещания (зеркало DTO). */
export interface MeetingProtocol {
  id: string;
  meeting_id: string;
  transcript_id: string | null;
  status: string;
  version: number;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  /** Краткое содержание совещания (L-2). */
  summary: string | null;
}

/** Пункт протокола: повестка/решение/поручение/разногласие + тема/тезис (topic/topic_point). */
export interface MeetingProtocolItem {
  id: string;
  protocol_id: string;
  kind: string;
  timecode: string | null;
  text: string;
  responsible_name: string | null;
  due_text: string | null;
  due_date?: string | null;
  status: string;
  needs_review: boolean;
  seq: number;
  /** «Спикер N» из диаризации (для topic_point/assignment). */
  speaker: string | null;
  /** Заголовок темы (для topic_point). */
  topic_title: string | null;
  /** Отдел (классификация L-2, D): дословное имя из справочника. */
  department_name: string | null;
  target_agent?: string | null;
  linked_entity_type?: string | null;
  linked_entity_id?: string | null;
}

/** Поручение из реестра (зеркало DTO `/agents/meetings/meetings/{id}/assignments`). */
export interface MeetingAssignment {
  id: string;
  meeting_id: string;
  text: string;
  assignee_name: string | null;
  /** Учётка исполнителя (users), если сопоставлен и привязан. */
  assignee_id: string | null;
  /** Исполнитель из справочника сотрудников (0166); null — не сопоставлен (HITL). */
  assignee_employee_id: string | null;
  due_text: string | null;
  due_date: string | null;
  status: string;
  created_at: string;
  /** Отдел (классификация L-2, D): дословное имя из справочника. */
  department_name: string | null;
  project_id?: string | null;
  contract_id?: string | null;
  target_agent?: string | null;
  linked_entity_type?: string | null;
  linked_entity_id?: string | null;
}

/** «Моё поручение» — поручение с заголовком совещания (личный список по учётке). */
export interface MyAssignment {
  id: string;
  meeting_id: string;
  meeting_title: string;
  text: string;
  assignee_name: string | null;
  due_text: string | null;
  due_date: string | null;
  status: string;
  created_at: string;
}

/** Действующее решение из накопительного реестра (N; зеркало Rust `SeriesDecision`).
 *  Отдел наследуется от совещания целиком, если пункт не классифицирован (блок E). */
export interface RegisterDecision {
  meeting_id: string;
  meeting_title: string;
  scheduled_at: string | null;
  protocol_id: string;
  item_id: string;
  /** decision | control (вид пункта протокола). */
  kind: string;
  timecode: string | null;
  text: string;
  department_id: string | null;
  department_name: string | null;
}

/** Поручение накопительного реестра (N): поля поручения + заголовок/дата совещания-
 *  источника (зеркало Rust `RegistryAssignment`). */
export interface RegisterAssignment {
  id: string;
  meeting_id: string;
  meeting_title: string;
  scheduled_at: string | null;
  text: string;
  assignee_name: string | null;
  assignee_id: string | null;
  assignee_employee_id?: string | null;
  due_text: string | null;
  due_date: string | null;
  status: string;
  department_id: string | null;
  department_name: string | null;
  project_id?: string | null;
  contract_id?: string | null;
  target_agent?: string | null;
  linked_entity_type?: string | null;
  linked_entity_id?: string | null;
}

/** Живая сводка серии (зеркало JSON GET /series/{id}/summary): действующие решения +
 *  открытые поручения со ссылками на протоколы + штамп актуальности. Без LLM. */
export interface MeetingSeriesSummary {
  series_id: string;
  decisions: RegisterDecision[];
  assignments: MeetingAssignment[];
  generated_at: string;
}

/** Найденный пункт архива протоколов (этап 3.1; зеркало Rust `ArchiveMatch`) — цитата
 *  совещание+протокол+пункт+таймкод для RAG-поиска. `score` — ts_rank / 1-cosine. */
export interface ArchiveMatch {
  chunk_id: string;
  meeting_id: string;
  protocol_id: string;
  item_id: string | null;
  series_id: string | null;
  department_id: string | null;
  department_name: string | null;
  meeting_title: string;
  scheduled_at: string | null;
  kind: string;
  timecode: string | null;
  content: string;
  score: number;
}

/** Ответ поиска по архиву: найденные пункты + метка времени. */
export interface ArchiveSearchResult {
  matches: ArchiveMatch[];
  generated_at: string;
}

/** RAG-ответ по архиву: синтезированный текст + цитаты-источники (§3.1 — непустой
 *  ответ несёт хотя бы одну цитату; зеркало Rust `ArchiveAnswer`). */
export interface ArchiveAnswerResult {
  answer: string;
  citations: ArchiveMatch[];
  generated_at: string;
}

/** Отдел из справочника (классификация решений/поручений по отделам, D). */
export interface MeetingDepartment {
  id: string;
  name: string;
  active: boolean;
  created_at: string;
  deleted_at: string | null;
}

/** Сотрудник из справочника (VK chat_id для авто-сопоставления участников, 0157). */
export interface MeetingEmployee {
  id: string;
  full_name: string;
  vk_chat_id: string | null;
  department: string | null;
  position: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Счётчик совещаний по отделу (папки витрины, блок E). null-отдел — «без отдела». */
export interface MeetingDepartmentCount {
  department_id: string | null;
  department_name: string | null;
  count: number;
}

/** Участник совещания (для адресной рассылки протокола в VK Teams). */
export interface MeetingParticipant {
  id: string;
  meeting_id: string;
  name: string;
  /** Адрес VK Teams (чат/учётка) для рассылки; null → без рассылки. */
  vk_chat_id: string | null;
  user_id: string | null;
  created_at: string;
}

/** Итог массового добавления участников списком (`POST …/participants/import`). */
export interface ParticipantImportResult {
  added: number;
  /** Уже были в списке (по email или ФИО) либо повторялись в самом списке. */
  already: number;
  /** Пустые строки. */
  skipped: number;
  /** Добавлены, но не найдены в справочнике сотрудников (имя и адрес — из списка). */
  not_in_directory: string[];
}

/** Виды пунктов протокола в порядке вывода. */
export const PROTOCOL_KINDS: readonly string[] = [
  'agenda',
  'decision',
  'assignment',
  'disagreement',
];

/** Подпись вида пункта протокола (неизвестный → сам код). */
export function protocolKindLabel(kind: string): string {
  switch (kind) {
    case 'agenda':
      return 'Повестка';
    case 'decision':
      return 'Решения';
    case 'assignment':
      return 'Поручения';
    case 'disagreement':
      return 'Разногласия';
    case 'topic':
      return 'Тема';
    case 'topic_point':
      return 'Тезис';
    default:
      return kind;
  }
}

/** Статусы поручения для ручной смены (постановщик/ответственный). */
export const ASSIGNMENT_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'pending', label: 'ожидает' },
  { value: 'in_progress', label: 'в работе' },
  { value: 'done', label: 'выполнено' },
  { value: 'cancelled', label: 'отменено' },
];

/** Подпись статуса поручения (неизвестный → сам код). */
export function assignmentStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'ожидает';
    case 'in_progress':
      return 'в работе';
    case 'done':
      return 'выполнено';
    case 'cancelled':
      return 'отменено';
    case 'overdue':
      return 'просрочено';
    default:
      return status;
  }
}

/** Тон статуса поручения: выполнено — success, просрочено — danger, иначе нейтраль. */
export function assignmentStatusTone(status: string): 'success' | 'danger' | 'neutral' {
  if (status === 'done') return 'success';
  if (status === 'overdue') return 'danger';
  return 'neutral';
}

/** Сегмент транскрипта (зеркало DTO): спикер + таймкоды (сек) + текст. */
export interface MeetingTranscriptSegment {
  id: string;
  speaker: string;
  t_start: number;
  t_end: number;
  text: string;
  seq: number;
}

/** Транскрипт совещания (метаданные + полный текст). */
export interface MeetingTranscript {
  id: string;
  meeting_id: string;
  language: string | null;
  duration_sec: number | null;
  speaker_count: number;
  full_text: string;
}

/** Секунды → таймкод `чч:мм:сс` (мм:сс при длительности < часа). */
export function fmtTimecodeSec(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const pad = (n: number) => String(n).padStart(2, '0');
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return hh > 0 ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}

/** Целевые модули для маршрутизации поручений совещания («Единый организм»). */
export const TARGET_AGENT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'progress', label: 'Контроль строительства (предписание)' },
  { value: 'pto', label: 'Инженер ПТО (РД / ИД / КС)' },
  { value: 'mto', label: 'Снабжение (МТО / поставки)' },
  { value: 'statut', label: 'Юрист (договоры / претензии)' },
  { value: 'smeta', label: 'Сметный отдел (Гранд-Смета / ФЕР / ГЭСН)' },
  { value: 'econ', label: 'Экономика и финансы (БДР / БДДС)' },
  { value: 'field', label: 'Стройплощадка / Прораб' },
  { value: 'geodesy', label: 'Геодезия' },
  { value: 'logist', label: 'Логистика и транспорт' },
  { value: 'kadry', label: 'Кадры и HR' },
  { value: 'dispatcher', label: 'Канцелярия / Диспетчер' },
];

export function targetAgentLabel(code: string | null | undefined): string {
  switch (code) {
    case 'progress':
    case 'director':
      return 'Контроль строительства';
    case 'pto':
      return 'Инженер ПТО';
    case 'mto':
    case 'supply':
      return 'Снабжение';
    case 'statut':
      return 'Юрист';
    case 'smeta':
      return 'Сметный отдел';
    case 'econ':
      return 'Экономика и финансы';
    case 'field':
      return 'Стройплощадка';
    case 'geodesy':
      return 'Геодезия';
    case 'logist':
      return 'Логистика';
    case 'kadry':
      return 'Кадры';
    case 'dispatcher':
    case 'assist':
      return 'Канцелярия';
    default:
      return code ?? '';
  }
}
